import React, { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Chip,
  Typography,
  CircularProgress,
  Alert,
  Tooltip,
  useTheme,
} from '@mui/material';
import NotesIcon from '@mui/icons-material/Notes';
import { MaterialReactTable } from 'material-react-table';
import { useAPI } from '../../../Hooks/useAPIs';
import { useReusableTable } from '../../TablePanel/tablePanel';
import { TimezoneContext } from '../../TimezoneContext/TimezoneContext';
import { useContext } from 'react';
import Paper from '@mui/material/Paper';

const REQUEST_TYPE_LABELS = {
  ownership_transfer: 'Ownership Transfer',
  new_device: 'New Device',
  registration_renewal: 'Registration Renewal',
  tailscale_approval: 'Tailscale Approval',
  device_lost: 'Device Lost',
  device_replacement: 'Device Replacement',
  device_repair: 'Device Repair',
  device_return: 'Device Return',
  device_update_overwrite_admin: 'Device Update Overwrite Admin',
};

const DeviceRegistrationHistoryDialog = ({
  open,
  onClose,
  device,
}) => {
  const { apiCall } = useAPI();
  const { formatDateTime } = useContext(TimezoneContext);
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  const requestsHistoryQuery = useQuery({
    queryKey: ['device-requests', device?.id],
    queryFn: async () => {
      if (!device?.id) return { results: [] };
      const response = await apiCall('GET', `api/v1/devices/requests?device_id=${device.id}`);
      return response.data;
    },
    enabled: open && !!device?.id,
    keepPreviousData: true,
  });

  const data = useMemo(() => {
    const allData = requestsHistoryQuery.data?.results || [];
    return allData;
  }, [requestsHistoryQuery.data]);

  const getStatusChipColor = (status) => {
    const statusLower = status?.toLowerCase();
    if (statusLower === 'approved' || statusLower === 'active' || statusLower === 'completed') {
      return { bgcolor: '#4caf50', color: 'white' };
    }
    if (statusLower === 'denied' || statusLower === 'rejected' || statusLower === 'cancelled') {
      return { bgcolor: '#f44336', color: 'white' };
    }
    if (statusLower === 'pending' || statusLower === 'submitted') {
      return { bgcolor: '#ff9800', color: 'white' };
    }
    if (statusLower === 'in_progress' || statusLower === 'under_review' || statusLower === 'processing') {
      return { bgcolor: '#2196f3', color: 'white' };
    }
    return { bgcolor: '#9e9e9e', color: 'white' };
  };

  const getRequestTypeColor = (type) => {
    switch (type) {
      case 'ownership_transfer':
        return 'info';
      case 'new_device':
        return 'success';
      case 'registration_renewal':
      case 'tailscale_approval':
        return 'primary';
      case 'device_lost':
      case 'device_replacement':
        return 'error';
      case 'device_repair':
        return 'warning';
      case 'device_return':
        return 'default';
      default:
        return 'default';
    }
  };

  const columns = useMemo(
    () => [
      {
        accessorKey: 'request_type',
        header: 'Type',
        Cell: ({ cell, row }) => {
          const type = cell.getValue();
          const displayLabel = row.original.request_type_display || REQUEST_TYPE_LABELS[type] || type || 'N/A';
          return (
            <Chip
              label={displayLabel}
              size="small"
              color={getRequestTypeColor(type)}
              variant="outlined"
            />
          );
        },
      },
      {
        accessorKey: 'status',
        header: 'Status',
        Cell: ({ cell, row }) => {
          const status = cell.getValue();
          const displayStatus = row.original.status_display || status;
          const chipColor = getStatusChipColor(status);
          return (
            <Chip
              label={displayStatus || 'N/A'}
              size="small"
              sx={{
                backgroundColor: chipColor.bgcolor,
                color: chipColor.color,
                fontWeight: 'bold',
              }}
            />
          );
        },
      },
      {
        accessorKey: 'device_detail',
        header: 'Device',
        Cell: ({ cell, row }) => {
          const deviceDetail = cell.getValue();
          const macAddr = deviceDetail?.mac_addr || row.original.requested_mac_addr || 'N/A';
          return (
            <Typography variant="body2" fontWeight="500">
              {macAddr}
            </Typography>
          );
        },
      },
      {
        accessorKey: 'requested_by_detail',
        header: 'Requested By',
        Cell: ({ cell }) => {
          const user = cell.getValue();
          if (!user) return <Typography variant="body2" color="text.secondary">N/A</Typography>;
          return (
            <Box>
              <Typography variant="body2" fontWeight="500">
                {user.display_name || user.email}
              </Typography>
              <Typography variant="caption" color="textSecondary">
                {user.email}
              </Typography>
            </Box>
          );
        },
      },
      {
        accessorKey: 'requested_to_detail',
        header: 'Requested To',
        Cell: ({ cell }) => {
          const user = cell.getValue();
          if (!user) return <Typography variant="body2" color="text.secondary">N/A</Typography>;
          return (
            <Box>
              <Typography variant="body2" fontWeight="500">
                {user.display_name || user.email}
              </Typography>
              <Typography variant="caption" color="textSecondary">
                {user.email}
              </Typography>
            </Box>
          );
        },
      },
      {
        accessorKey: 'requested_at',
        header: 'Requested Date',
        Cell: ({ cell, row }) => {
          const date = cell.getValue() || row.original.requested_date;
          return date ? formatDateTime(date, 'YYYY-MM-DD HH:mm:ss') : <Typography variant="body2" color="text.secondary">N/A</Typography>;
        },
      },
      {
        accessorKey: 'reviewed_by_detail',
        header: 'Reviewed By',
        Cell: ({ cell }) => {
          const user = cell.getValue();
          if (!user) return <Typography variant="body2" color="text.secondary">N/A</Typography>;
          return (
            <Box>
              <Typography variant="body2" fontWeight="500">
                {user.display_name || user.email}
              </Typography>
              <Typography variant="caption" color="textSecondary">
                {user.email}
              </Typography>
            </Box>
          );
        },
      },
      {
        accessorKey: 'reviewed_at',
        header: 'Reviewed At',
        Cell: ({ cell, row }) => {
          const date = cell.getValue() || row.original.approved_date;
          return date ? formatDateTime(date, 'YYYY-MM-DD HH:mm:ss') : <Typography variant="body2" color="text.secondary">N/A</Typography>;
        },
      },
      {
        accessorKey: 'completed_at',
        header: 'Completed At',
        Cell: ({ cell, row }) => {
          const date = cell.getValue();
          const hasNotes = row.original.notes || row.original.reason || row.original.rejection_reason;
          return (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {date ? formatDateTime(date, 'YYYY-MM-DD HH:mm:ss') : <Typography variant="body2" component="span" color="text.secondary">N/A</Typography>}
              {hasNotes && (
                <Tooltip title="Click to view details">
                  <NotesIcon sx={{ fontSize: 18, color: 'primary.main', ml: 1 }} />
                </Tooltip>
              )}
            </Box>
          );
        },
      },
    ],
    [formatDateTime]
  );

  const customRenderDetailPanel = useMemo(
    () => ({ row }) => {
      const record = row.original;
      const additionalData = record.additional_data || {};
      const hasChanges = additionalData.changes && Object.keys(additionalData.changes).length > 0;
      const hasDetails = record.notes || record.reason || record.rejection_reason || 
                         record.requested_to_detail || record.requested_pi || 
                         record.requested_device_type || record.requested_mac_addr || hasChanges;
      
      if (!hasDetails) {
        return (
          <Box sx={{ p: 3 }}>
            <Typography variant="body2" color="text.secondary">
              No additional details available for this record.
            </Typography>
          </Box>
        );
      }

      return (
        <Box sx={{ p: 3, bgcolor: 'action.hover', borderTop: '1px solid', borderColor: 'divider' }}>
          <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600, color: 'text.primary' }}>
            Record Details
          </Typography>
          
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {record.requested_to_detail && (
              <Box>
                <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary', display: 'block', mb: 0.5 }}>
                  Transfer To
                </Typography>
                <Box
                  sx={{
                    p: 1.5,
                    bgcolor: 'background.paper',
                    borderRadius: 1,
                    border: '1px solid',
                    borderColor: 'divider',
                  }}
                >
                  <Typography variant="body2" fontWeight="500">
                    {record.requested_to_detail.display_name || record.requested_to_detail.email}
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    {record.requested_to_detail.email}
                  </Typography>
                </Box>
              </Box>
            )}

            {record.requested_mac_addr && (
              <Box>
                <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary', display: 'block', mb: 0.5 }}>
                  Requested MAC Address
                </Typography>
                <Box sx={{ p: 1.5, bgcolor: 'background.paper', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
                  <Typography variant="body2" fontFamily="monospace">{record.requested_mac_addr}</Typography>
                </Box>
              </Box>
            )}

            {record.requested_pi && (
              <Box>
                <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary', display: 'block', mb: 0.5 }}>
                  Requested PI
                </Typography>
                <Box sx={{ p: 1.5, bgcolor: 'background.paper', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
                  <Typography variant="body2">{record.requested_pi}</Typography>
                </Box>
              </Box>
            )}

            {record.requested_device_type && (
              <Box>
                <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary', display: 'block', mb: 0.5 }}>
                  Requested Device Type
                </Typography>
                <Box sx={{ p: 1.5, bgcolor: 'background.paper', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
                  <Typography variant="body2">{record.requested_device_type}</Typography>
                </Box>
              </Box>
            )}

            {record.reason && (
              <Box>
                <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary', display: 'block', mb: 0.5 }}>
                  Reason
                </Typography>
                <Box
                  sx={{
                    p: 1.5,
                    bgcolor: 'background.paper',
                    borderRadius: 1,
                    border: '1px solid',
                    borderColor: 'divider',
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-word',
                      lineHeight: 1.7,
                      color: 'text.primary',
                    }}
                  >
                    {record.reason}
                  </Typography>
                </Box>
              </Box>
            )}

            {record.rejection_reason && (
              <Box>
                <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary', display: 'block', mb: 0.5 }}>
                  Rejection Reason
                </Typography>
                <Box
                  sx={{
                    p: 1.5,
                    bgcolor: isDark ? 'rgba(244, 67, 54, 0.1)' : '#fff5f5',
                    borderRadius: 1,
                    border: '1px solid',
                    borderColor: isDark ? 'rgba(244, 67, 54, 0.3)' : '#fed7d7',
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-word',
                      lineHeight: 1.7,
                      color: 'error.main',
                    }}
                  >
                    {record.rejection_reason}
                  </Typography>
                </Box>
              </Box>
            )}

            {record.notes && (
              <Box>
                <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary', display: 'block', mb: 0.5 }}>
                  Notes
                </Typography>
                <Box
                  sx={{
                    p: 1.5,
                    bgcolor: 'background.paper',
                    borderRadius: 1,
                    border: '1px solid',
                    borderColor: 'divider',
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-word',
                      lineHeight: 1.7,
                      color: 'text.primary',
                    }}
                  >
                    {record.notes}
                  </Typography>
                </Box>
              </Box>
            )}

            {hasChanges && (
              <Box>
                <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary', display: 'block', mb: 1 }}>
                  Changes Made
                </Typography>
                {additionalData.updated_fields && additionalData.updated_fields.length > 0 && (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 0.5 }}>
                      Updated Fields:
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                      {additionalData.updated_fields.map((field) => (
                        <Chip
                          key={field}
                          label={field.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                          size="small"
                          color="primary"
                          variant="outlined"
                        />
                      ))}
                    </Box>
                  </Box>
                )}
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                  {Object.entries(additionalData.changes || {}).map(([field, change]) => {
                    const formatValue = (value) => {
                      if (value === null || value === undefined) return 'N/A';
                      if (typeof value === 'boolean') return value ? 'Yes' : 'No';
                      if (typeof value === 'object') {
                        if (value.id && (value.email || value.display_name)) {
                          return value.display_name || value.email || `User ID: ${value.id}`;
                        }
                        return JSON.stringify(value);
                      }
                      return String(value);
                    };

                    const oldValue = formatValue(change.old);
                    const newValue = formatValue(change.new);

                    return (
                      <Paper
                        key={field}
                        variant="outlined"
                        sx={{
                          p: 1.5,
                          bgcolor: 'background.paper',
                          borderColor: 'divider',
                        }}
                      >
                        <Typography variant="body2" fontWeight="600" sx={{ mb: 1 }}>
                          {field.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                          <Box sx={{ flex: 1, minWidth: 150 }}>
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                              Old Value:
                            </Typography>
                            {typeof change.old === 'object' && change.old !== null && change.old.id ? (
                              <Box sx={{ p: 1, backgroundColor: 'action.hover', borderRadius: 1 }}>
                                <Typography variant="body2" fontWeight="500">
                                  {change.old.display_name || change.old.email || `User ID: ${change.old.id}`}
                                </Typography>
                                {change.old.email && (
                                  <Typography variant="caption" color="text.secondary">
                                    {change.old.email}
                                  </Typography>
                                )}
                              </Box>
                            ) : (
                              <Chip
                                label={oldValue}
                                size="small"
                                color="default"
                                variant="outlined"
                                sx={{ maxWidth: '100%' }}
                              />
                            )}
                          </Box>
                          <Typography variant="body2" sx={{ color: 'text.secondary', px: 1 }}>→</Typography>
                          <Box sx={{ flex: 1, minWidth: 150 }}>
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                              New Value:
                            </Typography>
                            {typeof change.new === 'object' && change.new !== null && change.new.id ? (
                              <Box sx={{ p: 1, bgcolor: isDark ? 'rgba(33, 150, 243, 0.15)' : '#e3f2fd', borderRadius: 1 }}>
                                <Typography variant="body2" fontWeight="500">
                                  {change.new.display_name || change.new.email || `User ID: ${change.new.id}`}
                                </Typography>
                                {change.new.email && (
                                  <Typography variant="caption" color="text.secondary">
                                    {change.new.email}
                                  </Typography>
                                )}
                              </Box>
                            ) : (
                              <Chip
                                label={newValue}
                                size="small"
                                color="primary"
                                sx={{ maxWidth: '100%' }}
                              />
                            )}
                          </Box>
                        </Box>
                      </Paper>
                    );
                  })}
                </Box>
              </Box>
            )}
          </Box>
        </Box>
      );
    },
    [isDark]
  );

  const { table } = useReusableTable({
    columns,
    data,
    rowCount: data.length,
    isLoading: requestsHistoryQuery.isFetching,
    enableRowSelection: false,
    enableRowExpansion: true,
    enableCompareFiles: false,
    enableDefaultAddToCart: false,
    enableRowActions: false,
    enableEditing: false,
    getRowId: (row) => row.id,
    pagination: { pageIndex: 0, pageSize: 25 },
    manualPagination: false,
    muiPaginationProps: {
      color: 'primary',
      rowsPerPageOptions: [25, 50, 100],
    },
    muiTableContainerProps: { sx: { maxHeight: 'calc(100vh - 300px)', minHeight: '500px' } },
    renderDetailPanel: customRenderDetailPanel,
  });

  if (!device) return null;

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="xl" 
      fullWidth
      PaperProps={{
        sx: {
          maxHeight: '90vh',
          height: '90vh',
        }
      }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="h6" component="div">
            Device Request History
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Device: {device.mac_addr}
          </Typography>
        </Box>
      </DialogTitle>
      <DialogContent dividers sx={{ p: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        {requestsHistoryQuery.isFetching && data.length === 0 ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', p: 5, flex: 1 }}>
            <CircularProgress />
          </Box>
        ) : data.length === 0 ? (
          <Box sx={{ p: 3 }}>
            <Alert severity="info">
              No request history found for this device.
            </Alert>
          </Box>
        ) : (
          <Box sx={{ flex: 1, overflow: 'auto', p: 2 }}>
            <MaterialReactTable table={table} />
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        <Button onClick={onClose} variant="contained" size="medium">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DeviceRegistrationHistoryDialog;
